import {
  ColumnFilter,
  ColumnFiltersState,
  Updater,
} from "@tanstack/react-table";
import clsx from "clsx";
import { ChangeEvent, useEffect, useState } from "react";
import { Filter as FilterIcon } from "lucide-react";
import Filter, { FilterFormValues, FilterVariant } from "../filter";
import Input from "../input";

interface FullFilterInputsProps {
  id: string;
  filterVariant: FilterVariant;
  filterState: ColumnFilter | undefined;
  alwaysDisplay: boolean;
  setFilters: (updater: Updater<ColumnFiltersState>) => void;
}

export const FullFilter = (props: Readonly<FullFilterInputsProps>) => {
  const { id, filterVariant, filterState, alwaysDisplay, setFilters } = props;

  const [opened, setOpened] = useState(false);

  const onSave = (form: FilterFormValues) => {
    setFilters((prev) => {
      const newFilters: ColumnFiltersState = prev.filter(
        (filter) => filter.id !== id,
      );
      const filter = {
        id,
        value: form.values,
        operation: form.operation,
      };

      if (filter.value.length === 0) return newFilters;

      return [...newFilters, filter];
    });
  };

  return (
    <div
      className={clsx(
        clsx("inline opacity-0 group-hover:opacity-100", {
          "opacity-100": opened || alwaysDisplay,
        }),
      )}
    >
      <Filter
        id={id}
        variant={filterVariant}
        initialValue={{
          operation: filterState?.operation,
          values: (filterState?.value as any[]) ?? [],
        }}
        onSave={onSave}
        onDropdownOpen={() => setOpened(() => true)}
        onDropdownClose={() => setOpened(() => false)}
      >
        <FilterIcon
          size="15"
          className={clsx(
            "transition-transform duration-300 hover:text-primary",
          )}
        />
      </Filter>
    </div>
  );
};

export const SimpleFilterInput = ({
  onChange,
  initValue,
}: {
  initValue: string;
  onChange: (value: string) => void;
}) => {
  const [value, setValue] = useState(initValue);
  const onFilterInputChange = (e: ChangeEvent<HTMLInputElement>) =>
    setValue(e.target.value);

  useEffect(() => {
    const timeout = setTimeout(() => {
      onChange(value);
    }, 250);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <div
      className={clsx(
        "inline-grid items-center w-full",
        "font-normal text-sm py-1 px-2 focus:ring-primary focus:border-hunter-300",
        "text-gunmetal-800 enabled:bg-white disabled:bg-gray-200 transition-all",
        "border border-gunmetal-200 rounded-sm outline-hidden",
        "focus-within:ring-[3px]",
        "focus-within:ring-primary/25 focus-within:border-hunter-300",
      )}
    >
      <Input
        size="sm"
        type="text"
        className="p-0 border-0 ring-0 focus:ring-0"
        onChange={onFilterInputChange}
        value={value}
        style={{
          gridArea: "1 / 1 / 2 / 2",
        }}
      />
      <span
        style={{
          gridArea: "1 / 1 / 2 / 2",
          visibility: "hidden",
        }}
      >
        {value}
      </span>
    </div>
  );
};
